import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ScienceIcon from '@mui/icons-material/Science';
import HealingIcon from '@mui/icons-material/Healing';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { claimsApi, ApiError } from '../../../services/api';
import styles from './PastClaimsScreen.module.css';

interface Claim {
  id: string;
  claimNumber: string;
  date: string;
  provider: string;
  service: string;
  status: 'approved' | 'pending' | 'denied' | 'processing';
  totalAmount: number;
  insurancePaid: number;
  youOwe: number;
  type: 'medical' | 'dental' | 'vision' | 'pharmacy';
}

const PastClaimsScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [filteredClaims, setFilteredClaims] = useState<Claim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchClaims();
  }, []);

  useEffect(() => {
    filterClaims();
  }, [claims, searchQuery, selectedType, selectedStatus]);

  const fetchClaims = async () => {
    setIsLoading(true);
    try {
      // API only has data for years 2022-2024
      const currentYear = Math.min(new Date().getFullYear(), 2024);
      const result = await claimsApi.getSummary({ year: currentYear.toString() });
      
      if (result.success) {
        setClaims(result.data.claims);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterClaims = () => {
    let filtered = [...claims];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(claim =>
        claim.provider.toLowerCase().includes(query) ||
        claim.service.toLowerCase().includes(query)
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(claim => claim.type === selectedType);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(claim => claim.status === selectedStatus);
    }

    setFilteredClaims(filtered);
  };

  const getClaimIcon = (type: string) => {
    switch (type) {
      case 'medical':
        return <LocalHospitalIcon className={styles.claimIcon} />;
      case 'dental':
        return <MedicalServicesIcon className={styles.claimIcon} />;
      case 'pharmacy':
        return <HealingIcon className={styles.claimIcon} />;
      case 'vision':
        return <ScienceIcon className={styles.claimIcon} />;
      default:
        return <LocalHospitalIcon className={styles.claimIcon} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleClaimClick = (claim: Claim) => {
    navigate(`/claims/${claim.id}`);
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading claims...</p>
      </div>
    );
  }

  return (
    <div className={styles.pastClaimsScreen}>
      <div className={styles.depth0Frame0}>
        <div className={styles.depth1Frame0}>
          <div className={styles.depth2Frame0}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </button>
            <div className={styles.depth3Frame1}>
              <b className={styles.claims}>Claims</b>
            </div>
          </div>

          {/* Search Bar */}
          <div className={styles.searchSection}>
            <div className={styles.searchBar}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search claims..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <button 
              className={styles.filterButton}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FilterListIcon />
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={styles.filtersSection}>
              <div className={styles.filterGroup}>
                <label>Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">All Types</option>
                  <option value="medical">Medical</option>
                  <option value="dental">Dental</option>
                  <option value="vision">Vision</option>
                  <option value="pharmacy">Pharmacy</option>
                </select>
              </div>
              <div className={styles.filterGroup}>
                <label>Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="denied">Denied</option>
                </select>
              </div>
            </div>
          )}

          {/* Claims List */}
          <div className={styles.claimsList}>
            {filteredClaims.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No claims found</p>
              </div>
            ) : (
              filteredClaims.map((claim) => (
                <div 
                  key={claim.id} 
                  className={styles.depth2Frame2}
                  onClick={() => handleClaimClick(claim)}
                >
                  <div className={styles.depth3Frame01}>
                    {getClaimIcon(claim.type)}
                    <div className={styles.depth4Frame1}>
                      <div className={styles.depth5Frame0}>
                        <div className={styles.routineCheckup}>{claim.service}</div>
                      </div>
                      <div className={styles.depth5Frame1}>
                        <div className={styles.drEmilyCarter}>{claim.provider}</div>
                      </div>
                      <div className={styles.claimDetails}>
                        <span className={styles.claimDate}>{formatDate(claim.date)}</span>
                        <span className={styles.claimAmount}>{formatCurrency(claim.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className={styles.chevronIcon} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastClaimsScreen;